import { useTranslates } from 'providers/translates';
import { useAppSelector } from 'hooks';
import styles from '../../../About/styles.module.scss';
import { Link } from 'react-router-dom';
import { Language } from 'providers/translates/enums';

import { TProfile } from 'api/http-client/profileTypes';
import { useAuth } from 'providers/auth-provider';
import { isVac } from 'utilities/is-vac';
import { Helmet } from 'react-helmet-async';

export const ContentVAC = () => {
  const { translates: t, selectedLang } = useTranslates();
  const isFrench = selectedLang === Language.FRENCH;
  const user = useAuth().user as unknown as TProfile;
  const editableQuilt = useAppSelector((state) => state.quilt.editableQuilt);
  
  const nameRow = `Name associated with your account: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRow = `Email address associated with your account: ${user?.email ?? ''}\n`;
  const quiltNameRow = `Name of the quilt square you need help with: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRow = `Issue or concern:`;
  
  const nameRowVac = `Name associated with your account: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRowVac = `Email address associated with your account: ${user?.email ?? ''}\n`;
  const quiltNameRowVac = `Name of the quilt square you need help with: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRowVac = `Issue or concern:`;
  
  const nameRowFr = `Nom associé à votre compte: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRowFr = `Adresse courriel associée à votre compt: ${user?.email ?? ''}\n`;
  const quiltNameRowFr = `Nom du carreau de courtepointe pour lequel vous avez besoin d’aide: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRowFr = `De quoi s’agit-il?`;
  
  const translationsHelmet = {
    descriptionEN: "Learn about how to create and invite others to collaborate on a tribute, allow viewers to add messages or salutes, or search tributes. You can also explore frequently asked questions.",
    descriptionFR: 'Découvrez comment créer un hommage, inviter d’autres personnes à collaborer, permettre aux visiteurs d’ajouter des messages ou des saluts, ou faire une recherche d’hommages. Vous pouvez également consulter la foire aux questions.',
    titleEN: 'Saluting Service | How to Create a Memorial',
    titleFR: 'Saluons leur service | Comment créer un hommage'
  } as const;


  let contactUsEmail: { subject: string; body: string; };

  if (isVac()) {
    contactUsEmail = isFrench ? {
      subject: encodeURIComponent('J’ai une question concernant SaluonsLeurService.ca'),
      body: encodeURIComponent(
        `\n\n\nN.B. – Si vous êtes un utilisateur actuel du site SaluonsLeurService.ca et que vous avez besoin d’assistance, veuillez nous fournir les renseignements suivants pour nous aider à vous faciliter la tâche.\n\n${nameRowFr}${emailRowFr}${quiltNameRowFr}${issueOrConcernRowFr}`
      ),
    } : {
      subject: encodeURIComponent('I have a question about SalutingService.ca'),
      body: encodeURIComponent(
        `\n\n\nNote: If you are a current SalutingService.ca user and are looking for assistance, please provide us with the following information so we can help you as easily as possible:\n\n${nameRowVac}${emailRowVac}${quiltNameRowVac}${issueOrConcernRowVac}`
      ),
    }
  } else {
    contactUsEmail = {
      subject: encodeURIComponent('I have a question about the Canadian Healing Quilt'),
      body: encodeURIComponent(
        `\n\n\nNote: If you are a current Canadian Healing Quilt user and are looking for assistance please provide us with the following information so we can help you as easily as possible:\n\n${nameRow}${emailRow}${quiltNameRow}${issueOrConcernRow}`
      )
    }
  }
  
  const anchorContactUsEmail = () => {
    return (
      <a href={`mailto:${isFrench ? 'info@portailpallatif.ca' : 'info@virtualhospice.ca'}?subject=${contactUsEmail.subject}&body=${contactUsEmail.body}`}>
        {isFrench ? 'info@portailpallatif.ca' : 'info@virtualhospice.ca'}
      </a>
    )
  }

  return (
    <>
      {isVac() && (
        <Helmet>
          <meta name="description" content={isFrench ? translationsHelmet?.descriptionFR : translationsHelmet?.descriptionEN} />
          <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
        </Helmet>
      )}
      <h2 className={styles.titleH2}>{isFrench ? 'Trouver un carreau de courtepointe' : 'Search for a quilt square'}</h2>
      <p className={styles.bodyText}>
        {isFrench ? (
          <>
            Pour trouver un carreau de courtepointe, utilisez l’outil de recherche en page d'accueil et entrez le nom de la personne.
          </>
        ) : (
          <>
            To find a quilt square, use the search feature on the home page and enter the person’s name.
          </>
        )}
      </p>

      <h2 className={styles.titleH2}>{isFrench ? 'Créer un carreau de courtepointe' : 'Create a quilt square'}</h2>
      <p className={styles.bodyText}>
        {isFrench ? (
          <>
            Vous pouvez utiliser un ordinateur, un portable, une tablette ou un téléphone intelligent pour créer un carreau de courtepointe. 
            La création d'un carreau de courtepointe est une activité personnelle – vous pouvez prendre tout le temps voulu.
          </>
        ) : (
          <>
            You can use a computer, laptop, tablet, or smartphone to create a quilt square. Creating a quilt square is 
            personal – you can take as much time as you need.
          </>
        )}                                                                                          
      </p>
      <ul className='colorNavy'>
        <li className={styles.list_item}>
          <span className={styles.list_dot}></span>
          {isFrench ? (
            <>
              Pour commencer, sélectionnez « Créer un hommage ».
            </>
          ) : (
            <>
              To get started, select “Create a Memorial.”
            </>
          )}
        </li>
        <li className={styles.list_item}>
          <span className={styles.list_dot}></span>
          {isFrench ? (
            <>
              Créez un compte. Vous devrez indiquer votre prénom et nom, votre adresse courriel, votre code postal et un mot de passe. 
              Vous recevrez un courriel de Saluons leur service avec un lien cliquable pour pour confirmer votre compte. Si vous ne 
              voyez pas ce courriel dans votre boîte de réception, regardez dans votre dossier « Pourriel ».
            </>
          ) : (
            <>
              Create an account. You will be asked for your full name, email address, postal code, and a password. 
              You will receive an email from Saluting Service to confirm your account by clicking a link. If you don’t 
              see this email in your Inbox, check your “Junk” or “Spam” folder.
            </>
          )}
        </li>
        <li className={styles.list_item}>
          <span className={styles.list_dot}></span>
          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            {isFrench ? 'Créez une courtepointe. Suivez les instructions pas à pas pour:' : 'Create a quilt. Follow the step-by-step instructions to:'}
            <ul >
              <li className={styles.list_item}>
                <span className={styles.list_dot}></span>{isFrench ? 'Ajouter des détails pertinents sur le service de la personne.' : 'Add relevant details about the person’s service.'}
              </li>
              <li className={styles.list_item}>
                <span className={styles.list_dot}></span>{isFrench ? `Inviter d'autres personnes à participer.` : 'Invite others to help create the quilt square.'}
              </li>
              <li className={styles.list_item}>
                <span className={styles.list_dot}></span>{isFrench ? 'Ajouter des photos et du texte.' : 'Add photos and text.'}
              </li>
              <li className={styles.list_item}>
                <span className={styles.list_dot}></span>{isFrench ? `Permettre aux visiteurs d'ajouter des messages, des histoires et des saluts.` : 'Allow visitors to add messages, stories, and salutes.'}
              </li>
            </ul>
          </div>
        </li>
        <li className={styles.list_item}>
          <span className={styles.list_dot}></span>
          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            {isFrench ? 'Partagez votre courtepointe. Publiez votre carreau.' : 'Share your quilt. Publish your square.'}
            <ul>
              <li className={styles.list_item}>
                <span className={styles.list_dot}></span>
                {isFrench ? (
                  <>
                    Partagez le carreau publiquement sur SaluonsLeurService.ca, ou choisissez de le garder privé et de ne le partager qu'avec les personnes de votre choix.
                  </>
                ) : (
                  <>
                    Share the square publicly on SalutingService.ca, or choose to keep it private and share only with people you choose.
                  </>
                )}
              </li>
              <li className={styles.list_item}>
                <span className={styles.list_dot}></span>
                {isFrench ? (
                  <>
                    Partagez le carreau publiquement sur SaluonsLeurService.ca, ou choisissez de le garder privé et de ne le partager qu'avec les personnes de votre choix.
                  </>
                ) : (
                  <>
                    Share the quilt square with family, friends, colleagues, and others using a message generated by SalutingService.ca and by posting on social media.
                  </>
                )}
              </li>
            </ul>
          </div>
        </li>
      </ul>

      <h2 className={styles.titleH2}>{isFrench ? 'Messages, souvenirs et saluts' : 'Messages, memories, and salutes'}</h2>
      <p className={styles.bodyText}>
        {isFrench ? (
          <>
            Cette option permet aux visiteurs de partager des messages ou des récits, ou de faire des « saluts » dans une section à part du carreau de courtepointe. 
            Le ou la titulaire a la possibilité d’approuver ou de supprimer les messages ou les saluts.
          </>
        ) : (
          <>
            This optional feature allows visitors to share messages, stories, or “salutes” in a separate section on the quilt square. 
            If the account holder chooses this option, they can approve or delete any messages or salutes.
          </>
        )}
      </p>
      <p className={styles.bodyText}>
        {isFrench ? (
          <>
            Quand vous regardez une courtepointe pour laquelle il y a des « Messages, souvenirs et saluts », vous pouvez ajouter un message ou un récit, 
            ou laisser votre nom avec un « salut » en signe de soutien. C’est gratuit.
          </>
        ) : (
          <>
            If you are viewing a quilt that has “Messages, memories, and salutes” available, you can add a message or story, 
            or leave your name and a “salute” to show support. There is no cost for this.
          </>
        )}
      </p>

      <h2 className={styles.titleH2}>{isFrench ? `Besoin d'inspiration?` : 'Need some inspiration?'}</h2>
      <p className={styles.bodyText}>
        {isFrench ? 'Cliquez sur les liens ci-dessous pour voir des exemples de carreaux finis.' : 'Click the links below to see examples of completed quilt squares.'}
      </p>
      <ul className='colorNavy'>
        <li className={styles.list_item}>
          <span className={styles.list_dot}></span>
          <Link 
            className={styles.customLink}
            to={'/Healing-Quilt-Sample-Memorial.pdf'} 
            target="__blank" 
            rel="noreferrer"
          >
            {isFrench ? 'Exemple 1' : 'Example 1'}
          </Link>
        </li>
        <li className={styles.list_item}>
          <span className={styles.list_dot}></span>
          <Link 
            className={styles.customLink}
            to={'/Healing-Quilt-Sample-Memorial.pdf'} 
            target="__blank" 
            rel="noreferrer"
          >
            {isFrench ? 'Exemple 2' : 'Example 2'}
          </Link>
        </li>
      </ul>
      <h2 className={styles.titleH2}>{t?.howToUse?.userSupportSection?.title ?? 'User support'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.userSupportSection?.firstPart ?? (
          <>
            We have tried to ensure you have a positive experience, but technical difficulties can occur. If you have trouble using SalutingService.ca, please refer to the “Frequently asked questions” below. If you need further support, email
          </>
        )}{' '}{anchorContactUsEmail()}.
      </p>

      <h2 className={styles.titleH2}>{t?.howToUse?.questionsSection?.title ?? 'Frequently asked questions'}</h2>
      <h3 className={styles.titleH3}>
        {t?.howToUse?.questionsSection?.firstSubtitle ?? (
          <>
            Is there a cost for creating a memorial?
          </>
        )}
      </h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.firstParagraph ?? (
          <>
           No. Developing and maintaining a tribute is completely free for people living in Canada. There are no hidden fees and no options for costly services. No one will ever try to sell you anything.
          </>
        )}
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.secondSubtitle ?? 'I didn’t get a confirmation email when I signed up. What should I do?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.secondParagraph ?? (
          <>
            SalutingService.ca automatically sends a confirmation email to the email address you used to make your account. Check your “Junk” or “Spam” folder for the confirmation email to verify your account. If you still need help, email us at
          </>
        )}{' '}{anchorContactUsEmail()}.
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.thirdSubtitle ?? 'Can I use a smartphone or tablet?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.thirdParagraph ?? (
          <>
           Yes. You can use it on smartphones, tablets, or personal computers. SalutingService.ca is compatible with all platforms using any browser (e.g., Chrome, Safari).
          </>
        )}
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.fourthSubtitle ?? 'What if I have other questions?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.fourthParagraph ?? 'If you have questions, email us at'}{' '}
       {anchorContactUsEmail()}
        .
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.fifthSubtitle ?? 'What if I have technical issues?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.fifthParagraph ?? (
          <>
            If you have trouble accessing the Canadian Healing Quilt, logging in, or using any of the features, please email us at
          </>
        )}{' '}
       {anchorContactUsEmail()}
        .
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.sixthSubtitle ?? 'How can I help support this project?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.sixthParagraph ?? 'To help us maintain the HealingQuilt.ca,'}{' '}
        {!isVac() ? (
          <a 
          href="https://www.canadahelps.org/en/charities/the-international-centre-for-dignity-and-palliative-care-inc/" 
          className={styles.customLink} 
          target="__blank"
        >{t?.howToUse?.questionsSection?.donateLink ?? 'donate here'}</a>
        ) : (
        <a 
          href={isFrench ? "https://www.canadahelps.org/fr/organismesdebienfaisance/the-international-centre-for-dignity-and-palliative-care-inc/" :  "https://www.canadahelps.org/en/charities/the-international-centre-for-dignity-and-palliative-care-inc/" }
          className={styles.customLink} 
          target="__blank"
        >{t?.howToUse?.questionsSection?.donateLink ?? 'donate here'}</a>)}
        .
      </p>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.seventhParagraph ?? (
          <>
            We welcome new partnerships and opportunities to expand the awareness 
            and use of the Canadian Healing Quilt across Canada. To learn more or get involved, email
          </>
        )}{' '}
       {anchorContactUsEmail()}
        .
      </p>
    </>
  );
};
